import { useEffect, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import camelCase from 'lodash/camelCase'
import startCase from 'lodash/startCase'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useQuery } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormSelect } from '@/components/ui/form/FormSelect.tsx'
import type { ContentTypeFiltersForm } from '@/dialogs/ContentTypeTikTokFiltersDialog/validation.ts'
import { contentTypeFiltersSchema } from '@/dialogs/ContentTypeTikTokFiltersDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { MultiSelect } from '@/modules/shifts/ui/dialogs/multi-select.tsx'
import { getTags } from '@/modules/tiktok-research/service'
import { TagsTypes } from '@/modules/tiktok-research/types.ts'
import { handleError } from '@/utils/error'

interface Props extends DialogProps {
  onSubmit: (values?: ContentTypeFiltersForm) => void
  initialValues?: ContentTypeFiltersForm
}

const getFieldName = camelCase

const initiallySelectedOption = { label: 'All', value: 'All' }

const fieldsOrder = [
  TagsTypes.Niche,
  TagsTypes.Ethnicity,
  TagsTypes.Hair_color,
  TagsTypes.Body_type,
]

export const ContentTypeTikTokFiltersDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
}: Props) => {
  const form = useForm<ContentTypeFiltersForm>({
    defaultValues: initialValues,
    resolver: zodResolver(contentTypeFiltersSchema),
  })

  // Need for options validation update
  useWatch({
    control: form.control,
  })

  const { data: tagsList, refetch: refetchContentTypeTags } = useQuery(
    'tiktokContentTypeTags',
    getTags,
    {
      onError: (error) => {
        handleError(
          error,
          'Failed to get content type tags',
          refetchContentTypeTags,
        )
      },
    },
  )

  const handleSubmit = form.handleSubmit((values) => {
    const dirtyFields = Object.entries(values).filter(([_key, value]) =>
      Array.isArray(value)
        ? value.length !== 0
        : value !== initiallySelectedOption.value,
    )
    const isDirty = dirtyFields.length > 0

    if (!isDirty) {
      onSubmit(undefined)

      return onClose()
    }
    onSubmit(Object.fromEntries(dirtyFields))
    onClose()
  })

  useEffect(() => {
    if (tagsList) {
      tagsList.forEach(({ type, isMulti }) => {
        const fieldName = getFieldName(type)

        form.setValue(
          fieldName,
          initialValues?.[fieldName] ??
            (isMulti ? [] : initiallySelectedOption.value),
        )
      })
    }
  }, [initialValues, form, tagsList])

  const sortedTagsList = useMemo(
    () =>
      tagsList?.sort(
        (a, b) => fieldsOrder.indexOf(a.type) - fieldsOrder.indexOf(b.type),
      ),
    [tagsList],
  )

  const handleClear = () => {
    if (tagsList) {
      tagsList.forEach(({ type, isMulti }) => {
        const fieldName = getFieldName(type)

        form.setValue(fieldName, isMulti ? [] : initiallySelectedOption.value)
      })
    }
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      bigDialog
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Dialog.Header className="mb-6">
            <Dialog.Title>Filter</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description>
            <div className="space-y-6">
              {sortedTagsList?.map(({ tags, type, isMulti }) => {
                const fieldName = getFieldName(type)

                const options = (
                  isMulti ? [] : [{ label: 'All', value: 'All' }]
                ).concat(
                  tags.flatMap((tagGroups) =>
                    tagGroups.map((tag) => {
                      let isDisabled = false

                      const value = form.getValues(fieldName)

                      if (Array.isArray(value)) {
                        isDisabled =
                          !value.includes(String(tag.id)) &&
                          value.some((selectedTag) =>
                            tagGroups.find(
                              (tag) => String(tag.id) === selectedTag,
                            ),
                          )
                      }

                      return {
                        label: tag.value,
                        value: String(tag.id),
                        disabled: isDisabled,
                        tooltip: isDisabled
                          ? 'You have already selected contradictive tag'
                          : undefined,
                      }
                    }),
                  ),
                )
                const label = startCase(type.toLowerCase())

                return isMulti ? (
                  <Controller
                    key={type}
                    name={fieldName}
                    control={form.control}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-col gap-2">
                        <label className="text-text-default text-sm font-medium">
                          {label}
                        </label>
                        <MultiSelect
                          options={options}
                          entity={label}
                          key={type}
                          value={(value as string[]) ?? []}
                          searchPlaceholder={`Search ${label}`}
                          placeholder="All"
                          onChange={onChange}
                          className="w-full"
                          disableAllSelect
                        />
                      </div>
                    )}
                  />
                ) : (
                  <FormSelect
                    searchable
                    key={type}
                    name={fieldName}
                    label={label}
                    options={options}
                  />
                )
              })}
            </div>
          </Dialog.Description>
          <Dialog.Footer>
            <div className="flex justify-between w-full gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClear}
                className="flex-1"
              >
                Clear
              </Button>
              <Button
                type="submit"
                variant="default"
                className="flex-1"
              >
                Save
              </Button>
            </div>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
